import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  BarChart,
  Mail,
  X,
  Eye,
  DollarSign,
  Activity,
  Send,
  UserCheck,
  FileText,
  BookOpen,
  Clock,
  MapPin,
  Award,
  Gift,
  CreditCard,
  BadgePercent,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Settings,
  Building2,
  Map,
  Layers,
  Store as StoreIcon,
  Stethoscope,
  Package,
  Receipt,
  Upload,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { canAccess, SidebarFeature } from '../../config/roleAccess';
import { isUiRefreshNavEnabled } from '../../config/uiRefresh';
import { isAppointmentOptimizationEnabled } from '../../config/features';
import RefreshSidebarNav from './RefreshSidebarNav';

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, end, onClick }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) => `
      flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors
      ${isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }
    `}
  >
    {icon}
    <span className="truncate">{label}</span>
  </NavLink>
);

interface NavSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const NavSection: React.FC<NavSectionProps> = ({ title, children, defaultExpanded = true }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // Hide section entirely if no children render (all gated out).
  const childArray = React.Children.toArray(children).filter(Boolean);
  if (childArray.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 px-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="mb-1 flex w-full items-center justify-between px-2 py-1 text-xs font-semibold uppercase text-muted-foreground hover:text-foreground"
      >
        <span>{title}</span>
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {expanded && (
        <div className="flex flex-col gap-1">
          {childArray}
        </div>
      )}
    </div>
  );
};

interface ChainNavSpec {
  to: string;
  icon: React.ReactNode;
  label: string;
  feature: SidebarFeature;
}

const CHAIN_NAV_ITEMS: ReadonlyArray<ChainNavSpec> = [
  {
    to: '/app/chain/corporation',
    icon: <Building2 className="h-5 w-5" />,
    label: 'Corporation',
    feature: 'chain.corporation',
  },
  {
    to: '/app/chain/regions',
    icon: <Map className="h-5 w-5" />,
    label: 'Regions',
    feature: 'chain.regions',
  },
  {
    to: '/app/chain/districts',
    icon: <Layers className="h-5 w-5" />,
    label: 'Districts',
    feature: 'chain.districts',
  },
  {
    to: '/app/chain/stores',
    icon: <StoreIcon className="h-5 w-5" />,
    label: 'Stores',
    feature: 'chain.stores',
  },
  {
    to: '/app/chain/practices',
    icon: <Stethoscope className="h-5 w-5" />,
    label: 'Practices',
    feature: 'chain.practices',
  },
];

const Sidebar: React.FC<SidebarProps> = ({ open, setOpen }) => {
  const { user } = useAuth();
  const role = user?.role ?? null;

  // Close sidebar on mobile after navigation
  const handleNavItemClick = () => {
    if (window.innerWidth < 768) {
      setOpen(false);
    }
  };

  // Gate helper: returns the nav node only if the user can access the feature.
  const gated = (feature: SidebarFeature, node: React.ReactNode): React.ReactNode =>
    canAccess(role, feature) ? node : null;

  const visibleChainItems = CHAIN_NAV_ITEMS.filter((item) =>
    canAccess(role, item.feature)
  );

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-sidebar
        className={`
          fixed inset-y-0 left-0 z-30 w-80 transform overflow-y-auto bg-card shadow-lg transition-transform duration-200 ease-in-out md:relative md:w-64 md:translate-x-0
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between bg-card p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <img src="/images/logo.svg" alt="VARAi" className="h-7" />
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <span className="text-[15px] font-semibold text-gray-900 leading-tight">VARAi</span>
                <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded leading-none tracking-wider">BETA</span>
              </div>
              <span className="text-[11px] text-gray-400 font-medium leading-tight">Practice Studio</span>
            </div>
          </div>
          <button
            type="button"
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
            onClick={() => setOpen(false)}
          >
            <span className="sr-only">Close sidebar</span>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          {isUiRefreshNavEnabled() ? (
            <RefreshSidebarNav role={role} onNavigate={handleNavItemClick} />
          ) : (
          <nav className="flex flex-col gap-1">
            {gated(
              'dashboard',
              <NavItem
                to="/app"
                icon={<Home className="h-5 w-5" />}
                label="Dashboard"
                end
                onClick={handleNavItemClick}
              />
            )}

            {gated(
              'iris',
              <NavItem
                to="/app/iris"
                icon={<Sparkles className="h-5 w-5" />}
                label="IRIS AI Assistant"
                onClick={handleNavItemClick}
              />
            )}

            {gated(
              'settings',
              <NavItem
                to="/app/settings"
                icon={<Settings className="h-5 w-5" />}
                label="Settings"
                onClick={handleNavItemClick}
              />
            )}

            <NavSection title="Knowledge">
              {gated(
                'knowledge',
                <NavItem
                  to="/app/knowledge"
                  icon={<BookOpen className="h-5 w-5" />}
                  label="Knowledge Base"
                  onClick={handleNavItemClick}
                />
              )}
            </NavSection>

            <NavSection title="Patient Management">
              {gated(
                'patient_management',
                <NavItem
                  to="/patients"
                  icon={<Users className="h-5 w-5" />}
                  label="Patients"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'patient_management',
                <NavItem
                  to="/appointments"
                  icon={<Calendar className="h-5 w-5" />}
                  label="Appointments"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'orders',
                <NavItem
                  to="/app/orders"
                  icon={<Receipt className="h-5 w-5" />}
                  label="Orders"
                  onClick={handleNavItemClick}
                />
              )}
            </NavSection>

            <NavSection title="Inventory">
              {gated(
                'inventory',
                <NavItem
                  to="/app/inventory"
                  icon={<Package className="h-5 w-5" />}
                  label="Stock"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'imports',
                <NavItem
                  to="/app/imports"
                  icon={<Upload className="h-5 w-5" />}
                  label="Import data"
                  onClick={handleNavItemClick}
                />
              )}
            </NavSection>

            {visibleChainItems.length > 0 && (
              <NavSection title="Chain">
                {visibleChainItems.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    onClick={handleNavItemClick}
                  />
                ))}
              </NavSection>
            )}

            <NavSection title="Analytics">
              {gated(
                'analytics',
                <NavItem
                  to="/app/analytics"
                  icon={<BarChart className="h-5 w-5" />}
                  label="Dashboard"
                  end
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'analytics',
                <NavItem
                  to="/app/analytics/demographics"
                  icon={<Users className="h-5 w-5" />}
                  label="Demographics"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'analytics',
                <NavItem
                  to="/app/analytics/revenue"
                  icon={<DollarSign className="h-5 w-5" />}
                  label="Revenue"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'analytics',
                <NavItem
                  to="/app/analytics/practice-optimization"
                  icon={<Activity className="h-5 w-5" />}
                  label="Practice Optimization"
                  onClick={handleNavItemClick}
                />
              )}
              {isAppointmentOptimizationEnabled() &&
                gated(
                  'analytics',
                  <NavItem
                    to="/app/analytics/appointment-optimization"
                    icon={<Clock className="h-5 w-5" />}
                    label="Appointment Optimization"
                    onClick={handleNavItemClick}
                  />
                )}
              {gated(
                'analytics',
                <NavItem
                  to="/app/analytics/contact-lens"
                  icon={<Eye className="h-5 w-5" />}
                  label="Contact Lens Analytics"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'analytics',
                <NavItem
                  to="/app/analytics/location"
                  icon={<MapPin className="h-5 w-5" />}
                  label="Location Analytics"
                  onClick={handleNavItemClick}
                />
              )}
            </NavSection>

            <NavSection title="Marketing">
              {gated(
                'marketing',
                <NavItem
                  to="/app/marketing"
                  icon={<Mail className="h-5 w-5" />}
                  label="Overview"
                  end
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'marketing',
                <NavItem
                  to="/app/marketing/campaigns"
                  icon={<Send className="h-5 w-5" />}
                  label="Campaigns"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'marketing',
                <NavItem
                  to="/app/marketing/segments"
                  icon={<UserCheck className="h-5 w-5" />}
                  label="Segments"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'marketing',
                <NavItem
                  to="/app/marketing/templates"
                  icon={<FileText className="h-5 w-5" />}
                  label="Templates"
                  onClick={handleNavItemClick}
                />
              )}
            </NavSection>

            <NavSection title="Loyalty Program">
              {gated(
                'loyalty',
                <NavItem
                  to="/app/loyalty"
                  icon={<Award className="h-5 w-5" />}
                  label="Dashboard"
                  end
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'loyalty',
                <NavItem
                  to="/app/loyalty/rewards"
                  icon={<Gift className="h-5 w-5" />}
                  label="Rewards Catalog"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'loyalty',
                <NavItem
                  to="/app/loyalty/members"
                  icon={<Users className="h-5 w-5" />}
                  label="Members"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'loyalty',
                <NavItem
                  to="/app/loyalty/tiers"
                  icon={<BadgePercent className="h-5 w-5" />}
                  label="Tier Management"
                  onClick={handleNavItemClick}
                />
              )}
              {gated(
                'loyalty',
                <NavItem
                  to="/app/loyalty/transactions"
                  icon={<CreditCard className="h-5 w-5" />}
                  label="Transactions"
                  onClick={handleNavItemClick}
                />
              )}
            </NavSection>
          </nav>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
